import {
  getDataFeedData,
  getDynamicLinkStats,
  getLinkTrackedHtml,
  getCampaignLinkStats as getRawLinkData,
  getSegmentUsers,
  getUserData,
} from 'api/CampaignAnalyticsAPI';
import { fetchTrackingErrorsByContentId } from 'api/MessagingInsightsAPI';
import { fetchCampaignByIdQuery, fetchTemplateByIdQuery } from 'api/queries';
import { selectUserRbacPermissions } from 'core/userContext';
import { formatEditorValueFromJsObj } from 'modules/aceEditor';
import { cspTag } from 'modules/secureIframe';
import { addNegativeToast } from 'modules/toastContainer';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useGraphql } from 'utils/hooks/useGraphql';
import { isEmpty } from '@iterable/utils';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { removeScriptFullDoc } from 'utils/script';
import { getUrlParams } from 'utils/urlParams';
import { useGetDataFeedIds } from '../components/dynamicHeatmaps/hooks/useGetDataFeedIds';
import { UserDataLevel } from '../constants';
import { selectSelectedVariant, selectTemplateId } from '../selectors';
import { DynamicHeatmapContext, DynamicHeatmapContextState } from './DynamicHeatmapContext';
import { useTimeFiltersContext } from './TimeFiltersContext';

enum GetUserDataType {
  Email = 'email',
  UserId = 'userId',
}

export const DynamicHeatmapContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { search } = useLocation();
  const { campaignId } = getUrlParams(search);
  const [userId, setUserId] = useState<string>('');
  const templateId = useSelector(selectTemplateId);
  const [messageContent, setMessageContent] = useState<string>('');
  const [userList, setUserList] = useState<string[]>([]);
  const [userPage, setUserPage] = useState(1);
  const [userCount, setUserCount] = useState(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState<string>('{}');
  const [dataFeedContent, setDataFeedContent] = useState('{}');
  const [dataFeedNames, setDataFeedNames] = useState([]);
  const [originalData, setOriginalData] = useState<string>('{}');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [dynamicLinkStats, setDynamicLinkStats] = useState<any[]>([]);
  const [rawLinkData, setRawLinkData] = useState<any[]>([]);
  const [augmentedLinkData, setAugmentedLinkData] = useState<any[]>([]);
  const [templateUrlData, setTemplateUrlData] = useState<any[]>([]);
  const selectedVariant = useSelector(selectSelectedVariant) || {};
  const [loadedIframe, setLoadedIframe] = useState(0);
  const [showLegacy, setShowLegacy] = useState(false);
  const [hasTrackingErrors, setHasTrackingErrors] = useState(false);
  const [hoveredTooltipKey, setHoveredTooltipKey] = useState('');
  const { data: templateData = {} } = useGraphql(fetchTemplateByIdQuery, {
    variables: { id: parseInt(selectedVariant?.templateId || templateId, 10) },
  });
  const contentId = templateData?.template?.contentId;
  const { dateParams } = useTimeFiltersContext();
  const permissions = useSelector(selectUserRbacPermissions);
  const [userDataLevel, setUserDataLevel] = useState<UserDataLevel>(UserDataLevel.Restricted);
  const { data: campaignData = {} } = useGraphql(fetchCampaignByIdQuery, {
    variables: { id: parseInt(campaignId, 10) },
  });
  const dispatch = useDispatch();

  const { campaign = {} } = campaignData || {};
  const { lists = [], experiments = null } = campaign || {};

  const isCustomDataCheck = () => {
    return originalData !== userData;
  };

  // don't include templateId in link fetching data if user isn't looking at a particular exp variant
  const linkStatsTemplateId =
    experiments && !isEmpty(selectedVariant) ? templateData?.template?.id : null;

  useEffect(() => {
    if (templateId && dateParams) {
      const { email = '', itblUserId } = JSON.parse(userData ?? '{}');
      getDynamicLinkStats({
        id: campaignId,
        startDate: dateParams?.startTime?.toISOString(),
        endDate: dateParams?.endTime?.toISOString(),
        templateId: linkStatsTemplateId,
        userUUID: itblUserId,
        userEmail: email,
      })
        .then(({ data }) => {
          setDynamicLinkStats(data ?? []);
        })
        .catch(() => {
          setDynamicLinkStats([]);
        });

      getRawLinkData({
        id: campaignId,
        startDate: dateParams?.startTime?.toISOString(),
        endDate: dateParams?.endTime?.toISOString(),
        urlOnlyView: true,
        templateId: linkStatsTemplateId,
        userUUID: itblUserId,
        userEmail: email,
      })
        .then((resp) => {
          setRawLinkData(
            (resp ?? []).map((el: any, idx: number) => ({
              ...el,
              id: idx,
            })),
          );
        })
        .catch(() => {
          setRawLinkData([]);
        });
    }
  }, [linkStatsTemplateId, dateParams, userData]);

  const dynamicLinkTotalCount = dynamicLinkStats.reduce((acc, curr) => {
    return curr.totalCount + acc;
  }, 0);
  const rawLinkDataCount = rawLinkData.reduce((acc, curr) => {
    return curr.totalCount + acc;
  }, 0);

  const hasLegacyAndDynamicClicks =
    dynamicLinkTotalCount !== 0 && dynamicLinkTotalCount < rawLinkDataCount;

  useEffect(() => {
    if (permissions.includes(PROJECT_PERMISSIONS.ViewAndExportUserData)) {
      if (lists?.length > 0) {
        setUserDataLevel(UserDataLevel.Dropdown);
        getSegmentUsers({
          page: 1,
          pageSize: 100,
          orderBy: null,
          sorting: null,
          searchQuery: {
            combinator: 'Or',
            searchQueries: [{ combinator: 'Or', searchQueries: [] }],
          },
          emailListId: lists[0].id,
        }).then(({ results, count }) => {
          setUserCount(count);
          setUserList(
            results.reduce((acc: any[], curr: any) => {
              if (curr?.email) {
                return [...acc, curr.email];
              }
              return acc;
            }, []),
          );
        });
      } else {
        setUserDataLevel(UserDataLevel.Input);
      }
    }
  }, [lists]);

  useEffect(() => {
    if (lists?.length > 0 && userDataLevel === UserDataLevel.Dropdown && userPage > 1) {
      getSegmentUsers({
        page: userPage,
        pageSize: 100,
        orderBy: null,
        sorting: null,
        searchQuery: { combinator: 'Or', searchQueries: [{ combinator: 'Or', searchQueries: [] }] },
        emailListId: lists[0].id,
      }).then(({ results }) => {
        setUserList([
          ...userList,
          ...results.reduce((acc: any[], curr: any) => {
            if (curr?.email) {
              return [...acc, curr.email];
            }
            return acc;
          }, []),
        ]);
      });
    }
  }, [userPage]);

  useEffect(() => {
    if (contentId && templateId && templateData?.template) {
      setIsLoading(true);
      getLinkTrackedHtml(contentId, templateId, userData === '{}' ? undefined : userData)
        .then(({ data }: { data: string }) => {
          const parsedData = removeScriptFullDoc(data, cspTag);
          if (messageContent === parsedData) {
            setLoadedIframe(loadedIframe + 1);
          }
          setMessageContent(parsedData);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [templateData, userData, contentId]);

  const { dataFeedData: dataFeedIds } = useGetDataFeedIds(templateId);

  useEffect(() => {
    if (templateId && dataFeedIds && dataFeedIds.length > 0) {
      getDataFeedData(templateData?.template?.id || templateId).then(
        (data: Record<string, any>) => {
          setDataFeedContent(formatEditorValueFromJsObj(data?.data?.contents || {}));
          setDataFeedNames(data?.data?.feedNames || []);
        },
      );
    }
  }, [templateData, dataFeedIds]);

  useEffect(() => {
    if (!contentId) return;
    fetchTrackingErrorsByContentId(contentId)
      .then(({ data }) => setHasTrackingErrors(data?.hasErrors ?? false))
      .catch(() => setHasTrackingErrors(false));
  }, [contentId]);

  const onLoadUserData = useCallback((id: string) => {
    setIsLoading(true);
    getUserData(id, GetUserDataType.Email)
      .then(({ data }: { data: Record<string, any> }) => {
        const jsonStr = formatEditorValueFromJsObj(data);
        setUserData(jsonStr);
        setOriginalData(jsonStr);
      })
      .catch((error: any) => {
        setUserData('{}');
        dispatch(addNegativeToast('Unable to load user data', error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const loadUser = (uId: string) => {
    const trimmedId = uId?.trim();
    setUserId(trimmedId);
    onLoadUserData(trimmedId);
  };

  const context: DynamicHeatmapContextState = {
    messageContent,
    onLoadUserData,
    isLoading,
    userData,
    setUserData,
    userList,
    isCustomDataCheck,
    userId,
    setUserId,
    userCount,
    setUserPage,
    userPage,
    dataFeedContent,
    dataFeedNames,
    loadUser,
    isFullscreen,
    setIsFullscreen,
    dynamicLinkStats,
    rawLinkData,
    setDynamicLinkStats,
    setRawLinkData,
    templateId,
    campaignId,
    contentId,
    augmentedLinkData,
    setAugmentedLinkData,
    templateUrlData,
    setTemplateUrlData,
    userDataLevel,
    loadedIframe,
    setLoadedIframe,
    hasLegacyAndDynamicClicks,
    showLegacy,
    setShowLegacy,
    hoveredTooltipKey,
    setHoveredTooltipKey,
    hasTrackingErrors,
  };

  return (
    <DynamicHeatmapContext.Provider value={context}>{children}</DynamicHeatmapContext.Provider>
  );
};
